/**
 * Thankyou.js — Post-purchase confirmation page.
 *
 * Clears cart localStorage on mount, then polls the engine API every 8 seconds
 * for the Shopify order record (get-db-order-data.php). Displays a loading
 * state until the order is ready, then renders the full order summary including
 * billing/shipping address, line items, and totals.
 *
 * Props:
 *  - EngineURL {string}                   Base URL for the engine REST API.
 *  - Program_structure_images {Array}     Slug-to-image-URL mapping for product cards.
 *  - useLoggedIn {boolean}                Whether the user is authenticated.
 *  - ShowCreateAccountPopup {Function}    Opens the create-account modal for guests.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import axios from 'axios';

// ─── Icons ────────────────────────────────────────────────────────────────────
import { LoaderIcon } from "../../icons";



export const Thankyou = (props) => {

    const checkoutoken = localStorage.getItem("checkout_token");

    const StructureImages = localStorage.getItem("thank_you_image") !== null ? JSON.parse(localStorage.getItem("thank_you_image")) : [];

    const [UserOrderDetail, setUserOrderDetail] = useState(undefined);
    const [shopify_response_loaded, set_shopify_response_loaded] = useState(false);
    const [shopify_response_half_loaded, set_shopify_response_half_loaded] = useState(false);
    const { resetCart } = props; // ✅ destructured outside

    /**
     * Purchase complete → empty the cart. Resets the app's cart state (header
     * badge + sidebar update instantly, no reload) and clears the cart's
     * localStorage. Runs once on mount; checkout_token is preserved for the
     * order lookup below.
     */
    const resetCartCalled = useRef(false);
    useEffect(() => {
        if (resetCartCalled.current) return;
        resetCartCalled.current = true;
        resetCart();
    }, [resetCart]);

   

    const intervalStarted = useRef(false);

    useEffect(() => {
        if (intervalStarted.current) return;
        intervalStarted.current = true;

        var clearinterval = setInterval(function () {
            if (checkoutoken !== null && UserOrderDetail === undefined && !shopify_response_loaded) {
                axios.post(props.EngineURL + 'chk/get-db-order-data.php', { checkout_token: checkoutoken })
                    .then(function (response) {
                        set_shopify_response_half_loaded(true);
                        if (response.data.status === "success") {
                            if (JSON.parse(response.data.data.shopify_response) !== null) {
                                setUserOrderDetail(JSON.parse(response.data.data.shopify_response));
                                set_shopify_response_loaded(true);
                                clearInterval(clearinterval);
                            }
                            else {
                                setUserOrderDetail(undefined);
                            }
                        }
                    })
                    .catch(function (error) {
                        console.log(error);
                    });
            }
        }, 8000);
    }, [checkoutoken, UserOrderDetail, shopify_response_loaded, props.EngineURL])

    /**
     * Normalises a title for matching: strips the U+FFFD ("?") import artifact,
     * collapses whitespace and lower-cases — so the Shopify order title matches
     * the cart title saved in thank_you_image even when encoding/spacing differs.
     */
    const normTitle = (s) => (s || '').replace(/�/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

    /**
     * Looks up the product image URL for a line item by title using the
     * thank_you_image localStorage payload and Program_structure_images mapping.
     * @param {string} title - Product title from the Shopify order.
     * @returns {string} Image URL.
     */
    function getTags(title) {
        // Array (not a bare string) so the no-match fallback also returns a URL
        // from [0] — a string fallback would make imageurl[0] === 'h'.
        var imageurl = ['https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Seminar.jpg'];        

        StructureImages.forEach(function (item, value) {
            if (normTitle(item.title) === normTitle(title)) {
                imageurl = getProdcutImage(item.tag);
            }
        })
        
        return imageurl[0];
    }

    /**
     * Returns the product slug for a thank-you line item by matching title
     * against the thank_you_image localStorage payload.
     * @param {string} title - Product title from the Shopify order.
     * @returns {string} Slug string, or "/" if not found.
     */
    function getSlug(title) {
        var imageurl = '/';
        StructureImages.forEach(function (item, value) {
            if (normTitle(item.title) === normTitle(title)) {
                imageurl = item.slug;
            }
        })
        return imageurl;
    }

    /**
     * Returns the program record type ("Type") for a line item by matching title
     * against the thank_you_image payload (saved at checkout from getRecordType).
     * @param {string} title - Product title from the Shopify order.
     * @returns {string} Record type string, or "" if not found.
     */
    function getType(title) {
        var type = '';
        
        StructureImages.forEach(function (item, value) {
            if (normTitle(item.title) === normTitle(title)) {
                type = item.type || '';
            }
        })
        
        return type;
    }

    /**
     * Resolves the image URL from Program_structure_images by matching any tag
     * in tag_array against the image map keys.
     * @param {string[]} tag_array - Tags from the thank_you_image payload.
     * @returns {string[]} Single-element array containing the image URL.
     */
    function getProdcutImage(tag_array) {
        var imagepath = ["https://engine.leocussen.edu.au/sync/img/logo_updated.png"];
        var productTags = tag_array;
        const targetTags = [
            "medicare-billing-course",
            "medicare-billing-courses",
            "medicare-billing-course-menu",
            "medicare-billing-courses-menu",
            "professional-short-course",
            "professional-short-courses",
            "professional-short-course-menu",
            "professional-short-courses-menu"
        ];

        const showImage =
            Array.isArray(productTags) &&
            productTags.some(tag => targetTags.includes(tag));

        if (showImage) {
            imagepath = [
                'https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/LeoCussen_Brand-mark_RGB_Colour.png'
            ];            
        } else {
            imagepath = [
                'https://engine.leocussen.edu.au/sync/img/logo_updated.png'
            ]; 
        }
        console.log('imagepath',imagepath);
        // Guard: a missing/non-iterable tag would throw on .includes and break
        // the whole order render — fall back to the default image instead.       
        props.Program_structure_images.forEach(function (item, value) {
            var keyname = Object.keys(item);
            if (tag_array.includes(keyname.toString())) {
                imagepath = Object.values(item);
            }
        })
        return imagepath;
    }

    /** Formats a number to 2 decimal places with thousands-separator commas. */
    const numberWithCommas = (x) => {
        return parseFloat(x).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    /** Formats ISO date string to DD/MM/YYYY */
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    }

    const SidebarCard = () => (
        <div className="ty-sidebar-card">
            <p className="ty-need-help">Need help?</p>
            <div className="ty-contact">
                <a href="tel:1300 039 031">1300 039 031</a>
                <a href="mailto:registration@leocussen.edu.au" className="ty-get-in-touch">Get in touch</a>
            </div>
        </div>
    );

    const MessageCard = () => (
        <div className="ty-card ty-card--message">
            <div className="ty-message-body">
                <p className="ty-intro">Thank you for your purchase - your order confirmation/tax invoice is on it's way.</p>
                <p className="ty-body">Are you new to our online store? Create an account to view your orders, see your upcoming programs, access your digital library and keep track of your CPD points, all in one place. Click below to sign up or log in.</p>
                <p className="ty-body">Thanks for supporting Leo Cussen Centre for Law.</p>
            </div>
            <div className="ty-account-link">
                <Link to="/admin-profile">Visit your account</Link>
                <span>to view your purchases.</span>
            </div>
        </div>
    );

    if (checkoutoken == null) {
        return (
            <div className="thank_you_page">
                <Container>
                    <h1 className="ty-page-title">Thank you for your order</h1>
                    <div className="ty-layout">
                        <div className="ty-main">
                            <MessageCard />
                        </div>
                        <div className="ty-sidebar">
                            <SidebarCard />
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (shopify_response_half_loaded && UserOrderDetail === undefined) {
        return (
            <div className="thank_you_page">
                <Container>
                    <h1 className="ty-page-title">Thank you for your order</h1>
                    <div className="ty-layout">
                        <div className="ty-main">
                            <MessageCard />
                            <div className="ty-card ty-card--message">
                                <p className="ty-intro">Please wait, your order is being processed...</p>
                            </div>
                        </div>
                        <div className="ty-sidebar">
                            <SidebarCard />
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (UserOrderDetail !== undefined) {
        const billing = UserOrderDetail.billing_address;
        const shipping = UserOrderDetail.shipping_address && UserOrderDetail.shipping_address.length
            ? UserOrderDetail.shipping_address
            : billing;

        return (
            <div className="thank_you_page">
                <Container>
                    <h1 className="ty-page-title">Thank you for your order</h1>
                    <div className="ty-layout">
                        {/* ── Left column ── */}
                        <div className="ty-main">

                            {/* Card 1 — Thank you message */}
                            <MessageCard />

                            {/* Card 2 — Order details */}
                            <div className="ty-card ty-card--order">
                                <p className="ty-order-number">Order #{UserOrderDetail.order_number}</p>

                                <div className="ty-order-sections">
                                    {/* Billing + Shipping */}
                                    <div className="ty-address-row">
                                        {billing && (
                                            <div className="ty-address-col">
                                                <p className="ty-label">Billing Address</p>
                                                <div className="ty-address-text">
                                                    <p>{billing.first_name} {billing.last_name}</p>
                                                    {billing.company ? <p>{billing.company}</p> : null}
                                                    <p>{billing.address1}{billing.address2 ? ' ' + billing.address2 : ''}</p>
                                                    <p>{billing.city} {billing.province} {billing.zip}</p>
                                                    <p>{billing.country}</p>
                                                </div>
                                            </div>
                                        )}
                                        <div className="ty-address-col">
                                            <p className="ty-label">Shipping Address</p>
                                            <div className="ty-address-text">
                                                <p>{shipping.first_name} {shipping.last_name}</p>
                                                {shipping.company ? <p>{shipping.company}</p> : null}
                                                <p>{shipping.address1}{shipping.address2 ? ' ' + shipping.address2 : ''}</p>
                                                <p>{shipping.city} {shipping.province} {shipping.zip}</p>
                                                <p>{shipping.country}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Meta row */}
                                    <div className="ty-meta-row">
                                        <div className="ty-meta-col">
                                            <p className="ty-label">Price</p>
                                            <p className="ty-meta-value">${numberWithCommas(UserOrderDetail.subtotal_price)}</p>
                                        </div>
                                        <div className="ty-meta-col">
                                            <p className="ty-label">Date</p>
                                            <p className="ty-meta-value">{formatDate(UserOrderDetail.created_at)}</p>
                                        </div>
                                        <div className="ty-meta-col">
                                            <p className="ty-label">Type</p>
                                            <p className="ty-meta-value">
                                                {(() => {
                                                    // Program record type(s) — resolved from thank_you_image
                                                    // (saved at checkout), the same "Type" shown on the
                                                    // order/billing page. line_items' own product_type is empty.
                                                    const types = [...new Set((UserOrderDetail.line_items || [])
                                                        .map((li) => getType(li.title).trim())
                                                        .filter(Boolean))];
                                                    return types.length ? types.join(", ") : "—";
                                                })()}
                                            </p>
                                        </div>
                                        <div className="ty-meta-col">
                                            <p className="ty-label">Dietary Requirement</p>
                                            <p className="ty-meta-value">
                                                {UserOrderDetail.note_attributes && UserOrderDetail.note_attributes.length > 0
                                                    ? (UserOrderDetail.note_attributes.find(a => a.name && a.name.toLowerCase().includes('dietary')) || {}).value || 'No'
                                                    : 'No'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Card 3 — Products + totals */}
                            <div className="ty-card ty-card--items">
                                <div className="ty-products-list">
                                    {UserOrderDetail.line_items.map((product, idx) => (
                                        <div className="ty-product-block" key={idx}>
                                            <Link to={"/product/" + getSlug(product.title)}>
                                                <img
                                                    src={getTags(product.title)}
                                                    className="ty-product-image"
                                                    alt={product.title}
                                                />
                                            </Link>
                                            <div className="ty-product-info">
                                                <div className="ty-product-header">
                                                    <Link to={"/product/" + getSlug(product.title)} className="ty-product-title">{product.title}</Link>
                                                    <p className="ty-product-code">Code {product.sku}</p>
                                                </div>
                                                <p className="ty-product-price">${numberWithCommas(product.price)}</p>
                                            </div>
                                            <span className="ty-product-qty">x{product.quantity}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="ty-totals-block">
                                    <div className="ty-subtotal-row">
                                        <p className="ty-subtotal-label">Subtotal</p>
                                        <p className="ty-subtotal-value">${numberWithCommas(UserOrderDetail.subtotal_price)}</p>
                                    </div>
                                    {UserOrderDetail.shipping_lines.length > 0 && (
                                        <div className="ty-shipping-rows">
                                            {UserOrderDetail.shipping_lines.map((line, idx) => (
                                                <div className="ty-shipping-row" key={idx}>
                                                    <p className="ty-shipping-label">{line.code}</p>
                                                    <p className="ty-shipping-value">${numberWithCommas(line.price)}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="ty-order-total-row">
                                        <p className="ty-order-total-label">Order Total (Include GST)</p>
                                        <p className="ty-order-total-value">${numberWithCommas(UserOrderDetail.total_price)}</p>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* ── Right sidebar ── */}
                        <div className="ty-sidebar">
                            <SidebarCard />
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="pre_loader">
            <LoaderIcon />
            <h5>Please wait, your order is being processed</h5>
        </div>
    );
}
